import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { BriefcaseBusiness, Contact, ExternalLink } from 'lucide-react'
import {
  type Dispatch,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'

import { DisplayTextOnHoverTooltip } from 'components/brokers/needs_attention/NeedsAttentionTable'
import { type Broker } from 'components/brokers/types'
import ReimbursementsContext from 'components/context/ReimbursementsContext'
import {
  type ReimbursementsFilters,
  useGetReimbursements,
} from 'components/hooks/reimbursements/useGetReimbursementRequests'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable, {
  getHeaderCheckboxState,
  type PaginationMetadata,
} from 'components/reusable/table/TanstackTable'
import { Checkbox } from 'components/ui/checkbox'
import { adminReimbursementRequests } from 'rails_routes'
import { getLocaleDateString } from 'utils'

import AssignBrokersToReimbursement from './AssignBrokersToReimbursement'
import ReimbursementStateBadge from './ReimbursementState'
import { type Reimbursement, type User } from './types'

export const MAX_NUMBER_OF_SELECTED_ITEMS = 50

const columnHelper = createColumnHelper<Reimbursement>()

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      return (
        <div onClick={(e) => e.stopPropagation()} className='flex items-center'>
          <Checkbox
            checked={getHeaderCheckboxState(table)}
            onClick={table.getToggleAllPageRowsSelectedHandler()}
          />
        </div>
      )
    },
    cell: ({ row }) => {
      return (
        <div onClick={(e) => e.stopPropagation()} className='flex items-center'>
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onClick={row.getToggleSelectedHandler()}
          />
        </div>
      )
    },
  }),
  columnHelper.display({
    header: 'Actions',
    cell: ({ row }: { row: Row<Reimbursement> }) => {
      const { setFilters } = useContext(ReimbursementsContext)

      return (
        <div className='flex items-center gap-4'>
          <DisplayTooltipOnHover text='Open in a new tab'>
            <div
              className='cursor-pointer h-full'
              onClick={(e) => {
                e.preventDefault()
                window.open(
                  adminReimbursementRequests.show({ id: row.original.id }),
                  '_blank',
                  'noopener noreferrer'
                )
              }}
            >
              <ExternalLink
                size={20}
                className='stroke-current text-gray-600'
              />
            </div>
          </DisplayTooltipOnHover>
          <DisplayTooltipOnHover text='Filter this member'>
            <div
              className='cursor-pointer h-full'
              onClick={() => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  member_ids: [row.original.member_id],
                }))
              }}
            >
              <Contact size={20} className='stroke-current text-gray-600' />
            </div>
          </DisplayTooltipOnHover>
          <DisplayTooltipOnHover text='Filter this employer'>
            <div
              className='cursor-pointer h-full'
              onClick={() => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  employer_ids: [row.original.employer_id],
                }))
              }}
            >
              <BriefcaseBusiness
                size={20}
                className='stroke-current text-gray-600'
              />
            </div>
          </DisplayTooltipOnHover>
        </div>
      )
    },
  }),
  columnHelper.accessor('created_at', {
    header: (info) => (
      <SortableHeader<Reimbursement> {...info} headerText='Date' />
    ),
    cell: (info) => getLocaleDateString(new Date(info.getValue())),
    enableSorting: true,
  }),
  columnHelper.accessor('brokers', {
    header: 'Assignees',
    cell: ({ getValue, row }) => {
      const { id } = row.original
      return (
        <AssignBrokersToReimbursement
          id={id}
          assignedBrokers={getValue() || []}
        />
      )
    },
  }),
  columnHelper.accessor('state', {
    header: 'State',
    cell: (info) => <ReimbursementStateBadge {...info.row.original} />,
  }),
  columnHelper.accessor('member_name', {
    header: 'Member',
    cell: (info) => (
      <DisplayTextOnHoverTooltip
        text={info.getValue() || 'N/A'}
        classNames='max-w-40'
      />
    ),
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: (info) => `$${parseFloat(info.getValue().value).toFixed(2)}`,
  }),
  columnHelper.accessor('purpose', {
    header: 'Purpose',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('employer_name', {
    header: 'Employer',
    cell: (info) => (
      <DisplayTextOnHoverTooltip
        text={info.getValue() || 'N/A'}
        classNames='max-w-40'
      />
    ),
  }),
]

type ReimbursementsTableProps = {
  filters: ReimbursementsFilters
  setFilters: Dispatch<SetStateAction<ReimbursementsFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  currentUser?: User
  currentBroker?: Broker
}

const ReimbursementsTable = ({
  filters,
  pagination,
  setPagination,
  sorting,
  setSorting,
  rowSelection,
  setRowSelection,
}: ReimbursementsTableProps) => {
  const {
    data: reimbursements,
    isLoading,
    isError,
  } = useGetReimbursements({ filters, pagination, sorting })

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  const table = useReactTable({
    data: reimbursements?.data || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: (row) => {
      return (
        Object.keys(rowSelection).length < MAX_NUMBER_OF_SELECTED_ITEMS ||
            rowSelection[row.id] === true
      )
    },
    onRowSelectionChange: (updater) => {
      setRowSelection((prev) => {
        const newSelection =
              typeof updater === 'function' ? updater(prev) : updater
        const selectedRowIds = Object.keys(newSelection)
        if (selectedRowIds.length > MAX_NUMBER_OF_SELECTED_ITEMS) {
          return prev // Keep the previous selection if over the limit
        }
        return newSelection
      })
    },
    state: {
      pagination,
      sorting,
      rowSelection,
    },
  })

  useEffect(() => {
    if (reimbursements) {
      setPaginationMetadata(reimbursements.pagination)
    }
  }, [reimbursements?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='There are no reimbursement requests that need attention.'
      paginationLabelSingular='reimbursement'
      paginationMetadata={paginationMetadata}
      setSorting={setSorting}
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}

export default ReimbursementsTable
